import DescriptionTinyEditor from 'components/Dashboard/UserContent/ui/DescriptionTinyEditor';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { useCreatePrompt } from 'components/WritingAssistant/ui/Create';
import { useRef } from 'react';

const CreateConstituentContent = ({
  titleValues,
  section,
  phrasesValues,
  isLoading,
  reviewSuggestionFunction,
  loadingState,
}) => {
  const isMobile = useIsMobile();
  const { normalizeEditorValue } = useCreatePrompt();
  const [heightContainer, containerRef] = useHeightContainer();
  return (
    <>
      {loadingState ? (
        <div
          ref={containerRef}
          className='d-flex flex-column justify-content-center'
          style={{ minHeight: `${heightContainer}px` }}
        >
          <div>
            <div className='text-center pb-5 mx-auto waiting-screen'>
              <p className='mb-2'>
                <strong>AI assistant is creating your response to a constituent</strong>
              </p>
              <p className='title-h4'>
                <em>This could take up to about 30 seconds</em>
              </p>
            </div>
            <div className='waiting-bar mx-auto'>
              <div className='waiting-bar-indicator' />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className='d-lg-flex specific-top-padding-20'>
            <div className='col-lg-10 px-0 pr-lg-3 mb-5 mb-lg-0'>
              <h3 className='title-h5-m-bold mb-2'>Paste in message from constituent</h3>
              <p className='mb-4 mb-lg-0 light-50-text'>
                Paste the full message from the constituent that you want the assistant to draft a response to. You are
                responsible for making sure all information you submit is lawful and shared with consent.{' '}
              </p>
              {isMobile && <ConstituentMessage titleValues={titleValues} section={section} />}
            </div>
            <div className='col-lg-6 px-0 pl-lg-3'>
              <h3 className='title-h5-m-bold mb-2'>Instructions for this response</h3>
              <p className='mb-4 mb-lg-0 light-50-text'>
                Add additional context or guidance you want the assistant to use when drafting this response, including
                facts, preferences or formatting.
              </p>
              {isMobile && <ReplyInstructions phrasesValues={phrasesValues} />}
            </div>
          </div>
          <div className='d-none d-lg-flex  mt-4'>
            <div className='col-lg-10 px-0 pr-lg-3'>
              <ConstituentMessage titleValues={titleValues} section={section} />
            </div>
            <div className='col-lg-6 px-0 pl-lg-3'>
              <ReplyInstructions phrasesValues={phrasesValues} />
            </div>
          </div>
          <div className='pb-4 specific-top-padding-10'>
            <button
              className='block rounded-pill py-2 action-button w-100 mt-4'
              onClick={(e) => {
                reviewSuggestionFunction(e);
              }}
              disabled={isLoading || normalizeEditorValue(titleValues?.title) === ''}
            >
              {isLoading && <i className='fas fa-spinner fa-spin mr-2' />} Generate draft response
            </button>
          </div>
        </>
      )}
    </>
  );
};

const ConstituentMessage = ({ titleValues, section }) => {
  const editorRef = useRef();
  const editorToEdit = useRef();

  return (
    <div className='border rounded bg-main-white'>
      <div className='influence-description position-relative ai-assistant-editor ai-assistant-editor-message'>
        <DescriptionTinyEditor
          ref={editorRef}
          editorToEdit={editorToEdit}
          value={titleValues?.title}
          setValue={(value) => titleValues?.setTitle(value)}
          placeholder='Message from constituent'
          minHeight={134}
          maxHeight={134}
          addVerticalScroll
          customButtons={`bold | italic | numlist | bullist`}
          enableContextMenu={false}
          additionalStyles={
            'p {margin: 0; line-height: 1.4;} .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks) {cursor: text;}'
          }
          index={0}
        />
      </div>
      <div className='px-3 py-2 rounded-bottom bg-red-5 border-top'>
        <p className='mb-0 text-red-60'>
          Do not submit personal data from constituents.{' '}
          <a
            href='https://support.policymogul.com/en/articles/6930562-ai-assistant-for-parliamentarians'
            target={`_blank`}
            rel='noopener noreferrer'
            tabIndex={'-1'}
            className='text-underline text-red-60 text-hover-red'
          >
            Learn more.
          </a>
        </p>
      </div>
    </div>
  );
};

const ReplyInstructions = ({ phrasesValues }) => {
  const { setPhrases, phrases } = phrasesValues;
  const editorToEdit = useRef();
  const editorRef = useRef();

  return (
    <div className='rounded border h-100 bg-main-white'>
      <div className='influence-description position-relative ai-assistant-editor ai-assistant-editor-instructions'>
        <DescriptionTinyEditor
          ref={editorRef}
          editorToEdit={editorToEdit}
          value={phrases[0]}
          setValue={(value) => setPhrases([value])}
          placeholder='Add instructions (optional)'
          minHeight={177}
          maxHeight={177}
          addVerticalScroll
          customButtons={`bold | italic | numlist | bullist`}
          additionalStyles={
            'p {margin: 0; line-height: 1.4;} .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks) {cursor: text;}'
          }
        />
      </div>
    </div>
  );
};

export default CreateConstituentContent;
